import {useCallback, useState} from 'react';
import React from 'react';
import {useModalManager} from '@/components/basic/sequential-modal/ModalManagerProvider';
import {
  getYesterdayRewardAndPopup,
  GetYesterdayRewardAndPopupResponse,
} from '../services/dailyBettingBoard';
import {ModalType} from '../components/Modal/old-user';
import OldUser from '../components/Modal/old-user';
import Regress from '../components/Modal/regress';
import ExpirationStorage from '@utils/Storage/ExpirationStorage';
import globalStore from '@services/global.state';

/**
 * 按优先级顺序生成弹窗序列
 * 优先级顺序：REGRESSION, LUCK_BUILDING, WINNER_ALERT, CASH_PRIZE, POINTS_REWARD, INVITE_REWARD
 */
const generateModalSequence = (
  data: GetYesterdayRewardAndPopupResponse,
): ModalType[] => {
  const modals: ModalType[] = [];
  // 最高优先级：回归弹窗
  if (data.regressionInfo.regressionFlag && data.regressionInfo.bannerFlag) {
    modals.push(ModalType.REGRESSION);
  }

  // 按优先级顺序检查其他弹窗条件
  if (data.popupInfo.newUserRankNoRewardShowFlag) {
    modals.push(ModalType.LUCK_BUILDING);
  }
  if (data.popupInfo.newUserRankRewardShowFlag) {
    modals.push(ModalType.WINNER_ALERT);
  }
  if (data.rewardInfo.hasReward) {
    modals.push(ModalType.CASH_PRIZE);
  }
  if (
    data.popupInfo.inviteShowFlag &&
    (data.popupInfo.inviteShowValue ?? 0) > 0
  ) {
    modals.push(ModalType.POINTS_REWARD);
  }
  if (
    data.popupInfo.inviteDepositShowFlag &&
    (data.popupInfo.inviteDepositShowValue ?? 0) > 0
  ) {
    modals.push(ModalType.INVITE_REWARD);
  }

  return modals;
};

/**
 * 根据弹窗类型和数据创建弹窗组件
 */
const createModalComponents = (
  modalTypes: ModalType[],
  data: GetYesterdayRewardAndPopupResponse,
): React.ReactNode[] => {
  const components: React.ReactNode[] = [];

  modalTypes.forEach(type => {
    switch (type) {
      case ModalType.REGRESSION:
        components.push(
          React.createElement(Regress, {
            _bonusRate: data.regressionInfo.bonusRate,
          }),
        );
        break;
      case ModalType.CASH_PRIZE:
        // 检测是否今日已经弹过，没有就push
        const store = new ExpirationStorage(
          `OLD_USER_RANK_ACTIVITY_${globalStore.userInfo!.userId}`,
          1,
        );
        const {show} = store.get() || {};
        if (!show) {
          store.set({
            show: true,
          });
          components.push(
            React.createElement(OldUser, {
              modalType: type,
              amount: data.rewardInfo.rewardValue?.toString(),
              rank: data.rewardInfo.rankPosition,
            }),
          );
        }
        break;
      case ModalType.POINTS_REWARD:
        components.push(
          React.createElement(OldUser, {
            modalType: type,
            userCount: data.popupInfo.inviteShowCount || 0,
            amount: data.popupInfo.inviteShowValue?.toString(),
          }),
        );
        break;
      case ModalType.INVITE_REWARD: // 首充
        components.push(
          React.createElement(OldUser, {
            modalType: type,
            userCount: data.popupInfo.inviteDepositShowCount || 0,
            amount: data.popupInfo.inviteDepositShowValue || 0,
          }),
        );
        break;
      case ModalType.WINNER_ALERT:
        components.push(
          React.createElement(OldUser, {
            modalType: type,
          }),
        );
        break;
      case ModalType.LUCK_BUILDING:
        components.push(
          React.createElement(OldUser, {
            modalType: type,
          }),
        );
        break;
    }
  });

  return components;
};

/**
 * 处理日投注榜相关的弹窗显示逻辑
 * @param externalRegressionInfo 可选的外部回归信息，如果提供则避免重复接口调用
 */
export function useDailyBettingModals(
  externalRegressionInfo?: GetYesterdayRewardAndPopupResponse,
) {
  const {showModal} = useModalManager();
  const [processedData, setProcessedData] =
    useState<GetYesterdayRewardAndPopupResponse | null>(null);

  // 处理弹窗显示的主要逻辑
  const processModals = useCallback(async () => {
    try {
      let data: GetYesterdayRewardAndPopupResponse;

      // 优先使用外部提供的数据，避免重复调用接口
      if (externalRegressionInfo) {
        data = externalRegressionInfo;
        setProcessedData(data);
      } else if (processedData) {
        // 如果已经有处理过的数据，直接使用
        data = processedData;
      } else {
        // 兼容性：如果没有外部数据且未处理过，则自行获取
        data = await getYesterdayRewardAndPopup();
        setProcessedData(data);
      }

      const modalSequence = generateModalSequence(data);

      if (modalSequence.length > 0) {
        const modalComponents = createModalComponents(modalSequence, data);

        showModal({
          backdropBlur: true,
          modals: modalComponents,
          id: 'dailyBettingModals',
        });
      }
    } catch (error) {
      console.error('bettingModals:', error);
    }
  }, [showModal, externalRegressionInfo, processedData]);

  return {
    processModals,
  };
}
